import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const members = [
  {
    name: 'Polycarpos Yiorkadjis',
    role: 'Developer',
    affil: 'M.S. student in Computer Science, Stanford University',
  },
  {
    name: 'Harshvardhan Agarwal',
    role: 'Developer',
    affil: 'M.S. student in Computer Science, Stanford University',
  },
  {
    name: 'Ashish Goel',
    role: 'Project Lead',
    affil: 'Professor of Management Science and Engineering and (by courtesy) Computer Science, Stanford University',
  },
  {
    name: 'Rakesh Agrawal',
    role: 'Researcher',
    affil: 'Computer scientist, formerly Technical Fellow at Microsoft Search Labs',
  },
];

const s = {
  page:    { minHeight: '100vh', background: '#f9f9f9', display: 'flex', flexDirection: 'column' },
  wrap:    { maxWidth: 720, margin: '0 auto', padding: '72px 32px 88px', width: '100%' },
  h1:      { fontSize: '1.9rem', fontWeight: 700, color: '#1a2e4a', marginBottom: 16, lineHeight: 1.3 },
  h2:      { fontSize: '1.1rem', fontWeight: 700, color: '#1a2e4a', marginBottom: 16 },
  p:       { fontSize: '1rem', color: '#444', lineHeight: 1.75, marginBottom: 14 },
  pLast:   { fontSize: '1rem', color: '#444', lineHeight: 1.75 },
  hr:      { border: 'none', borderTop: '1px solid #e0e0e0', margin: '48px 0' },
  ol:      { paddingLeft: 20, margin: '0 0 14px', color: '#444', fontSize: '1rem', lineHeight: 1.75 },
  li:      { marginBottom: 6 },
  link:    { color: '#006CB8', textDecoration: 'none' },
  label:   { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#006CB8', display: 'inline-block', marginLeft: 8 },
};

function Homepage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={s.page}>

      {/* Navbar */}
      <nav className={`nav-bar${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">
          <span className="nav-brand">Stanford <span>Centro</span></span>
          <div className="nav-right">
            <button className="nav-btn" onClick={() => navigate('/login')}>Log In</button>
          </div>
        </div>
      </nav>

      <div style={s.wrap}>

        {/* Title */}
        <h1 style={s.h1}>Stanford Centro</h1>
        <p style={s.p}>
          Centro is a stablecoin-based payment platform built on Hyperledger Fabric 2.x.
          It allows users to send Centro directly to one another (instant transfer), or lock
          funds in a time-bounded escrow agreement that a designated third-party verifier can
          release or reject.
        </p>

        <hr style={s.hr} />

        {/* How it works */}
        <h2 style={s.h2}>How It Works</h2>
        <p style={s.p}>
          Each user starts with a wallet holding 100 Centro. Agreements are created by
          specifying a payee, an optional verifier, an amount, and an optional deadline.
          If no verifier is given, funds transfer immediately. Otherwise:
        </p>
        <ol style={s.ol}>
          <li style={s.li}><strong>Create Agreement</strong> — the payer defines the payee, verifier, amount, deadline, and conditions.</li>
          <li style={s.li}><strong>Funds Locked</strong> — the amount is deducted from the payer and held in a cryptographically secured escrow wallet on-chain.</li>
          <li style={s.li}><strong>Verification</strong> — if the verifier approves before the deadline, funds are released to the payee. Rejection or expiry returns them to the payer.</li>
        </ol>


        <hr style={s.hr} />

        {/* Cryptographic proof system */}
        <h2 style={s.h2}>Cryptographic Proof System</h2>
        <p style={s.p}>
          Three independent tiers of cryptographic proof allow any transaction to be verified
          without trusting the server:
        </p>
        <ol style={s.ol}>
          <li style={s.li}>
            <strong>Signed Transaction Receipt</strong> — a JSON document containing transaction
            details, signed with the Fabric admin's ECDSA private key. Anyone holding the receipt
            and the signing certificate can verify the signature offline without querying the
            blockchain.
          </li>
          <li style={s.li}>
            <strong>In-Block Merkle Proof</strong> — proves that a specific transaction is
            included in its block using an O(log n) sibling-hash path, computed from the
            block's transaction envelopes hashed with SHA-256.
          </li>
          <li style={s.li}>
            <strong>Block-Level Blockchain Merkle Tree</strong> — proves that a specific block
            is part of the full chain in O(log n) steps, reducing the cost of full-chain
            verification from O(N) to O(log N).
          </li>
        </ol>

        <hr style={s.hr} />

        {/* Team */}
        <h2 style={s.h2}>Team</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
          <tbody>
            {members.map((m, i) => (
              <tr
                key={m.name}
                style={{ borderTop: i === 0 ? '1px solid #e0e0e0' : undefined, borderBottom: '1px solid #e0e0e0' }}
              >
                <td style={{ padding: '14px 0', verticalAlign: 'top', width: '32%', paddingRight: 24 }}>
                  <span style={{ fontWeight: 600, color: '#1a2e4a' }}>{m.name}</span>
                </td>
                <td style={{ padding: '14px 0', verticalAlign: 'top', color: '#555', lineHeight: 1.55 }}>
                  {m.affil}
                  <span style={s.label}>{m.role}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

      </div>

      <footer className="page-footer" style={{ marginTop: 'auto' }}>
        Stanford Centro · Built on Hyperledger Fabric
      </footer>

    </div>
  );
}

export default Homepage;
